import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { BaseUrl } from "../Components/Constants/Constants";

export const useCourseEngagement = ({ courseId } = {}) => {
  const [sessionId, setSessionId] = useState(null);
  const heartbeatInterval = useRef(null);
  const idleTimer = useRef(null);

  // Start tracking
  const startTracking = async ({ moduleId, moduleName, moduleType, courseId }) => {
    if (sessionId) return; // Already tracking

    try {
      const res = await axios.post(
        `${BaseUrl}user-session-tracking/start`,
        { 
          course_id: courseId, 
          training_module_id: moduleId, 
          training_module_name: moduleName, 
          training_module_type: moduleType 
        },
        { withCredentials: true }
      );

      if (res.data?.session_id) {
        setSessionId(res.data.session_id);
        localStorage.setItem("session_id", res.data.session_id);
      }
    } catch (e) {
      console.error("❌ Error starting session tracking", e);
    }
  };

  // Send heartbeat
  const sendHeartbeat = async () => {
    if (!sessionId) return;
    
    try {
      const payload = { session_id: sessionId };
      if (courseId) payload.course_id = courseId;
      
      await axios.post(
        `${BaseUrl}user-session-tracking/heartbeat`, 
        payload, 
        { withCredentials: true }
      );
    } catch (e) {
      console.error("❌ Error sending heartbeat", e);
    }
  };

  // End tracking
  const endTracking = async () => {
    if (!sessionId) return;

    try {
      const payload = { session_id: sessionId };
      if (courseId) payload.course_id = courseId;
      
      await axios.post(
        `${BaseUrl}user-session-tracking/end`, 
        payload, 
        { withCredentials: true }
      );
    } catch (e) {
      console.error("❌ Error ending session tracking", e);
    } finally {
      // ✅ Clean up session state
      localStorage.removeItem("session_id");
      setSessionId(null);

      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
        heartbeatInterval.current = null;
      }
      
      if (idleTimer.current) {
        clearTimeout(idleTimer.current);
        idleTimer.current = null;
      }
    }
  };

  // Heartbeat interval (every 30 seconds)
  useEffect(() => {
    if (!sessionId) return;

    heartbeatInterval.current = setInterval(() => {
      sendHeartbeat();
    }, 30000); // every 30s

    return () => {
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
        heartbeatInterval.current = null;
      }
    };
  }, [sessionId]);

  // Idle detection (5 minutes of inactivity)
  useEffect(() => {
    if (!sessionId) return;

    const resetIdleTimer = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      
      idleTimer.current = setTimeout(() => {
        console.log("⏰ User idle for 5 minutes - ending session");
        endTracking();
      }, 5 * 60 * 1000); // 5 minutes
    };

    const events = ["mousemove", "keypress", "click", "scroll", "touchstart"];
    events.forEach(evt => window.addEventListener(evt, resetIdleTimer));
    
    // Initialize the timer
    resetIdleTimer();

    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      events.forEach(evt => window.removeEventListener(evt, resetIdleTimer));
    };
  }, [sessionId]);

  // Tab close / browser unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (sessionId) {
        const payload = JSON.stringify({ 
          session_id: sessionId, 
          ...(courseId ? { course_id: courseId } : {}) 
        });
        
        // Use sendBeacon for reliable delivery on page unload
        navigator.sendBeacon(`${BaseUrl}user-session-tracking/end`, payload);
        localStorage.removeItem("session_id");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [sessionId, courseId]);

  // Tab switch / page hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && sessionId) {
        console.log("👁️ Tab hidden - ending session");
        endTracking();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [sessionId, courseId]);

  return { startTracking, sendHeartbeat, endTracking, sessionId };
};